"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.privToPem = exports.pubToPem = exports.Keys = void 0;
const elliptic_1 = require("elliptic");
const bn_js_1 = __importDefault(require("bn.js"));
const eciesjs_1 = require("eciesjs");
const ethers_1 = require("ethers");
const functions_1 = require("./functions");
const ec = new elliptic_1.ec('secp256k1');
class Keys {
    /**
     * @param {string} privateKey
     * @param {string} publicKey
     */
    constructor({ privateKey, publicKey } = {}) {
        if (privateKey) {
            this._keyPair = ec.keyFromPrivate(privateKey, 'hex');
            this.privateKey = privateKey;
        }
        else if (publicKey) {
            this._keyPair = ec.keyFromPublic(publicKey, 'hex');
            this.privateKey = '';
        }
        else {
            this._keyPair = ec.genKeyPair();
            this.privateKey = this._keyPair.getPrivate('hex').padStart(64, '0');
        }
        this.publicKey = this._keyPair.getPublic(true, 'hex').padStart(66, '0');
    }
    getAddress() {
        return new ethers_1.Wallet(this.privateKey).address.toString();
    }
    /**
     * Decrypt the encrypted data that is given in hex format
     *
     * @example
     * ```typescript
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keysAlice = new Keys();
     * const keysBob = new Keys();
     * const data = 'test';
     * const encrypted = await keysAlice.encrypt(data, keysBob.publicKey);
     * const decrypted = await keysBob.decrypt(encrypted);
     * console.log(decrypted); // 'test'
     * ```
     *
     * @param {string} encrypted
     * @param {string} publicKey
     * @returns {Promise<string>}
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    decrypt(encrypted, publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const encryptedBuffer = Buffer.from(encrypted, 'hex');
            const privateKeyBuffer = Buffer.from(this.privateKey, 'hex');
            const data = yield (0, eciesjs_1.decrypt)(privateKeyBuffer, encryptedBuffer);
            return data.toString();
        });
    }
    /**
     * Encrypt the data that is given in utf-8 string
     *
     * @example
     * ```typescript
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keysAlice = new Keys();
     * const keysBob = new Keys();
     * const data = 'test';
     * const encrypted = await keysAlice.encrypt(data, keysBob.publicKey);
     * console.log(encrypted); // hex symbols string
     * ```
     *
     * @param {string} data
     * @param {string} publicKeyTo
     * @returns {Promise<string>}
     */
    encrypt(data, publicKeyTo) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKeyToBuffer = Buffer.from(publicKeyTo || this.publicKey, 'hex');
            const dataBuffer = Buffer.from(data);
            const encrypted = yield (0, eciesjs_1.encrypt)(publicKeyToBuffer, dataBuffer);
            return encrypted.toString('hex');
        });
    }
    /**
     * Sign the data
     *
     * @example
     * ```typescript
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keys = new Keys();
     * const data = 'test';
     * const signature = keys.sign(data);
     * console.log(signature); // 128 hex symbols string
     * ```
     *
     * @param {string} data
     * @param {string} privateKey
     * @returns {string}
     */
    sign(data, privateKey) {
        let keyPair = this._keyPair;
        if (privateKey) {
            keyPair = ec.keyFromPrivate(privateKey, 'hex');
        }
        const hash = (0, functions_1.sha256)(data);
        const signature = keyPair.sign(hash, 'hex', {
            canonical: true,
            pers: true,
        });
        return signature.r.toString(16, 64) + signature.s.toString(16, 64);
    }
    /**
     * Verify the signature
     *
     * @example
     * ```typescript
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keys = new Keys();
     * const data = 'test';
     * const signature = keys.sign(data);
     * console.log(keys.verify(signature)); // true
     * ```
     *
     * @param {string} data
     * @param {string} signature
     * @param {string} publicKey
     * @returns {boolean}
     */
    verify(data, signature, publicKey) {
        let keyPair = this._keyPair;
        if (publicKey) {
            keyPair = ec.keyFromPublic(publicKey, 'hex');
        }
        const r = new bn_js_1.default(signature.slice(0, 64), 16).toString('hex');
        const s = new bn_js_1.default(signature.slice(64, 128), 16).toString('hex');
        const hash = (0, functions_1.sha256)(data);
        return keyPair.verify(hash, { r, s });
    }
    /**
     * Generates new key pair for secp256k1 algorithm.
     *
     * @example
     * ```typescript
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keyPair = Keys.generateKeyPair();
     * console.log(keyPair.privateKey) // 64 hex symbols string
     * console.log(keyPair.publicKey) // 66 hex symbols string
     * ```
     *
     * @returns {KeyPair}
     */
    static generateKeyPair() {
        const keyPair = ec.genKeyPair();
        return {
            privateKey: keyPair.getPrivate('hex').padStart(64, '0'),
            publicKey: keyPair.getPublic(true, 'hex').padStart(66, '0'),
        };
    }
}
exports.Keys = Keys;
__exportStar(require("./models"), exports);
var functions_2 = require("./functions");
Object.defineProperty(exports, "pubToPem", { enumerable: true, get: function () { return functions_2.toPem; } });
Object.defineProperty(exports, "privToPem", { enumerable: true, get: function () { return functions_2.privToPem; } });
//# sourceMappingURL=index.js.map